import _ from 'underscore';

import { WindowStatus } from './asset-window';

// -1 means the total count is unknown (maybe infinite), so the windows count is unknown too
const INDETERMINATE = -1; // indeterminate
// -2 means the total count is not set yet
const UNSET = -2;

const MAX_ITEM_COUNT = 0x7FFFFFFF;

// the loading windows manager is used to keep track which pages are loading/loaded or not loaded yet
export class AssetsWindowManager {
  constructor(windowSize) {
    this.windowSize = windowSize;
  }

  resetWindows() {
    this.assetWindows = []; // array of WindowStatus
    this.totalAssetCount = UNSET;
    this.windowsCount = UNSET;
  }

  setTotalItemCount(totalAssetCount) {
    if (this.totalAssetCount !== UNSET) {
      return;
    }
    this.totalAssetCount = totalAssetCount >= 0 ? totalAssetCount : INDETERMINATE;
    this.windowsCount = totalAssetCount >= 0 && this.windowSize > 0 ? Math.ceil(totalAssetCount / this.windowSize) : INDETERMINATE;
  }

  // is any window loading?
  get isLoading() {
    const ret = _.some(this.assetWindows, w => w === WindowStatus.loading);
    return ret;
  }

  // all windows are loaded?
  get isCompleted() {
    // if the total count is not set, it means the loading is not started yet
    if (this.totalAssetCount === UNSET) {
      return false;
    }

    // if the total count is indeterminate, it will never be completed
    if (this.totalAssetCount === INDETERMINATE || this.windowsCount === INDETERMINATE) {
      return false;
    }

    // check status of all windows
    for (let i = 0; i < this.windowsCount; i += 1) {
      if (!this.assetWindows[i] || this.assetWindows[i] !== WindowStatus.loaded) {
        return false;
      }
    }

    return true;
  }

  getTotalAssetCount() {
    if (this.totalAssetCount === UNSET || this.totalAssetCount === INDETERMINATE) {
      return MAX_ITEM_COUNT;
    }
    return this.totalAssetCount || 0;
  }

  // return AssetWindow object, which contains the windowIndex of the next window to load
  // and if all windows are loaded, it will return -1
  getNextUnloadedWindow() {
    // the initial page load, item count is returned with the initial page data
    if (this.totalAssetCount === UNSET) {
      this.assetWindows = [WindowStatus.loading];
      return { windowIndex: 0, windowSize: this.windowSize };
    }

    // find the first unloaded window
    let windowIndex = -1;
    const length = this.windowsCount >= 0 ? this.windowsCount : this.assetWindows.length;
    for (let i = 0; i < length; i += 1) {
      // for error windows, retry them
      if (!this.assetWindows[i] || this.assetWindows[i] === WindowStatus.error) {
        this.assetWindows[i] = WindowStatus.loading;
        windowIndex = i;
        break;
      }
    }

    if (windowIndex !== -1) {
      return { windowIndex, windowSize: this.windowSize };
    }

    // it's maybe a infinite list
    if (this.totalAssetCount === INDETERMINATE) {
      // add a new loading window
      this.assetWindows.push(WindowStatus.loading);
      return { windowIndex: this.assetWindows.length - 1, windowSize: this.windowSize };
    }

    // no more windows to load
    return { windowIndex: -1, windowSize: this.windowSize };
  }

  // paremeter is the window object and status to set
  setWindowStatus({ windowIndex }, status) {
    this.assetWindows[windowIndex] = status;
  }
}

// compose multiple AssetsWindowManager for multiple sources
export class SequenceAssetsWindowManager {
  // sourceNames is an array of source names, like ['assetlibrary', 'stock']
  constructor(windowSize, sourceNames) {
    this.windowSize = windowSize;
    this.sourceNames = sourceNames;
    this.winMgrList = _.map(sourceNames, (sourceName) => {
      const mgr = new AssetsWindowManager(windowSize);
      mgr.sourceName = sourceName;
      return mgr;
    });
  }

  resetWindows() {
    _.each(this.winMgrList, (mgr) => {
      mgr.resetWindows();
    });
  }

  setTotalItemCount(totalAssetCount, winObj) {
    const { sourceName } = winObj;
    const mgr = _.find(this.winMgrList, { sourceName });
    if (mgr) {
      mgr.setTotalItemCount(totalAssetCount);
    }
  }

  get isLoading() {
    const ret = _.some(this.winMgrList, mgr => mgr.isLoading);
    return ret;
  }

  get isCompleted() {
    const ret = _.every(this.winMgrList, mgr => mgr.isCompleted);
    return ret;
  }

  getTotalAssetCount() {
    let count = 0;
    for (let i = 0; i < this.winMgrList.length; i += 1) {
      const cnt = this.winMgrList[i].totalAssetCount;
      if (cnt === INDETERMINATE || cnt === UNSET) {
        return MAX_ITEM_COUNT;
      }
      count += cnt || 0;
    }
    return count;
  }

  getNextUnloadedWindow() {
    // find the first incomplete window
    const winMgr = _.find(this.winMgrList, mgr => !mgr.isCompleted);
    if (winMgr) {
      const { windowIndex } = winMgr.getNextUnloadedWindow();
      //
      let indexOffset = 0; // sum of previous windows' totalAssetCount
      for (let i = 0; i < this.winMgrList.length; i += 1) {
        if (this.winMgrList[i] === winMgr) {
          break;
        }
        indexOffset += this.winMgrList[i].totalAssetCount;
      }
      // append sourceName to the window object
      return {
        windowIndex,
        windowSize: this.windowSize,
        sourceName: winMgr.sourceName,
        indexOffset, // sum of previous windows' totalAssetCount
      };
    }
    return { windowIndex: -1, windowSize: this.windowSize };
  }

  setWindowStatus(winObj, status) {
    const { sourceName } = winObj;
    const mgr = _.find(this.winMgrList, { sourceName });
    if (mgr) {
      mgr.setWindowStatus(winObj, status);
    }
  }

  getAssetCountBySource() {
    const ret = {};
    _.each(this.winMgrList, (mgr) => {
      ret[mgr.sourceName] = mgr.totalAssetCount;
    });
    return ret;
  }
}
